import { RefProps } from '@lib/types'
import { ConfigProvider, TimePicker } from 'antd'
import { RangePickerProps } from 'antd/lib/date-picker'
import zhTW from 'antd/lib/locale/zh_TW'
import cx from 'classnames'
import { any } from 'ramda'
import React from 'react'
import styled from 'styled-components'

type Props = RangePickerProps &
  RefProps & {
    onEnter?: (value: [moment.Moment | null, moment.Moment | null]) => void
  }

const TimeRangePicker = React.forwardRef<typeof TimePicker.RangePicker, Props>(
  ({ className = '', onChange, onEnter, value, ...props }, ref) => {
    const [_inputValue, setInputValue] = React.useState<
      [moment.Moment | null, moment.Moment | null] | null
    >(value || null)

    React.useEffect(() => {
      if (value?.[0] !== _inputValue?.[0] || value?.[1] !== _inputValue?.[1]) {
        setInputValue(value as [moment.Moment, moment.Moment])
      }
    }, [value])

    return (
      <ConfigProvider locale={zhTW}>
        <RangePickerStyled
          locale={zhTW.DatePicker}
          forwardRef={ref}
          order={false}
          className={cx('c-datepicker', className)}
          onChange={(value, dateString) => {
            onChange && onChange(value, dateString)
            setInputValue(value as [moment.Moment, moment.Moment])
          }}
          onKeyDown={e => {
            if (
              e.key === 'Enter' &&
              _inputValue?.length === 2 &&
              any(v => v !== null, _inputValue)
            ) {
              e.stopPropagation()
              onEnter && onEnter(_inputValue)
            }
          }}
          value={_inputValue}
          {...props}
        />
      </ConfigProvider>
    )
  }
)

const RangePickerStyled = styled(TimePicker.RangePicker)`
  & .ant-picker-clear {
    background: transparent;
  }
  &.ant-picker {
    border-radius: 4px;
    background-color: ${p => p.theme.datePicker.bg};
    border-color: ${p => p.theme.datePicker.borderColor};
    &:hover,
    &:focus {
      border-color: ${p => p.theme.datePicker.hoverAndFocusBorderColor};
    }
    > .ant-picker-active-bar {
      background: ${p => p.theme.datePicker.rangeActiveBarBg};
    }
    .ant-picker-separator,
    .ant-picker-suffix {
      color: ${p => p.theme.datePicker.placeholderColor};
    }
    > .ant-picker-input {
      > input {
        color: ${p => p.theme.datePicker.color};
        &::placeholder {
          color: ${p => p.theme.datePicker.placeholderColor};
        }
      }
      > input[disabled] {
        color: ${p => p.theme.datePicker.disabledColor};
      }
    }
    &.ant-picker-disabled {
      background: ${p => p.theme.datePicker.disabledBg};
      color: ${p => p.theme.datePicker.disabledColor};
    }
    > .ant-picker-clear svg[data-icon='close-circle'] {
      fill: ${p => p.theme.datePicker.clearIconFill};
    }
    > .ant-picker-suffix svg[data-icon='calendar'] {
      fill: ${p => p.theme.datePicker.calendarIconFill};
    }
    .ant-picker-clear {
      background: #000;
    }
  }
`

export default TimeRangePicker
